import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, ChevronDown, MapPin, Phone } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { SERVICES } from "@/data/services";
import { usePageMeta, SITE_BASE } from "@/utils/seo";

const PROVIDER = {
  "@type": "LocalBusiness",
  name: "HRG Service GmbH",
  telephone: "+41 78 223 80 87",
  email: "info@hrgservice.ch",
  address: { "@type": "PostalAddress", streetAddress: "Rüstenschwilerstrasse 2", postalCode: "5642", addressLocality: "Mühlau", addressCountry: "CH" },
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  usePageMeta(service && {
    title: service.seoTitle,
    description: service.seoDesc,
    path: `/leistungen/${service.slug}`,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: service.title,
        description: service.seoDesc,
        provider: PROVIDER,
        areaServed: ["Aargau", "Luzern", "Zürich", "Zug"],
        url: `${SITE_BASE}/leistungen/${service.slug}`,
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: service.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  });

  if (!service) return <Navigate to="/leistungen" replace />;
  const others = SERVICES.filter((s) => s.slug !== slug);

  return (
    <main className="pt-24" data-testid="service-detail-page">
      <section className="bg-mesh border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 py-16 lg:py-24">
          <Link to="/leistungen" data-testid="service-back-link" className="group mb-8 inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition-colors duration-200 hover:text-[#3B62A5]">
            <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-1" />
            Alle Leistungen
          </Link>
          <p className="font-tech text-xs uppercase tracking-[0.3em] text-[#3B62A5] font-semibold mb-5">{service.nr} — Leistung</p>
          <h1 data-testid="service-detail-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0A111E] leading-[1.08]">
            <MaskedLines lines={[service.title]} />
          </h1>
          <Reveal delay={0.3}>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">{service.intro}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <span className="glass-soft inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium text-slate-600"><MapPin className="h-4 w-4 text-[#3B62A5]" /> AG · LU · ZH · ZG</span>
              <span className="glass-soft inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium text-slate-600"><Check className="h-4 w-4 text-[#3B62A5]" /> Kostenlose Besichtigung</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <Reveal>
              <div className="glass-card group overflow-hidden rounded-3xl">
                <img src={service.img} alt={service.alt} className="aspect-[16/9] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-10 space-y-5">
                {service.paragraphs.map((p, i) => (
                  <p key={i} className="text-base text-slate-600 leading-relaxed">{p}</p>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <h2 className="mt-12 text-2xl sm:text-3xl font-bold tracking-tight text-[#0A111E]">Das ist inbegriffen</h2>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {service.points.map((p) => (
                  <li key={p} className="glass-card flex items-start gap-3 rounded-2xl p-4 text-sm text-slate-700">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#E8F0FE]"><Check className="h-3.5 w-3.5 text-[#3B62A5]" /></span>
                    {p}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.2}>
              <h2 className="mt-12 text-2xl sm:text-3xl font-bold tracking-tight text-[#0A111E]">Häufige Fragen</h2>
              <div className="mt-6 space-y-4" data-testid="service-faq-list">
                {service.faqs.map((f, i) => (
                  <details key={f.q} data-testid={`service-faq-${i}`} className="glass-card group rounded-2xl p-5">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-semibold text-[#0A111E]">
                      {f.q}
                      <ChevronDown className="h-4 w-4 shrink-0 text-[#3B62A5] transition-transform duration-200 group-open:rotate-180" />
                    </summary>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 space-y-6">
              <Reveal delay={0.1}>
                <div className="rounded-3xl border border-white/20 bg-gradient-to-br from-[#3B62A5] to-[#2A477A] p-7 text-white shadow-[0_20px_50px_rgba(59,98,165,0.25)]">
                  <p className="text-lg font-semibold leading-snug">Interesse an {service.title}?</p>
                  <p className="mt-2 text-sm text-white/80 leading-relaxed">Wir erstellen Ihnen gerne eine unverbindliche Offerte nach kostenloser Besichtigung.</p>
                  <Link to={`/kontakt?service=${service.slug}`} data-testid="service-cta-offerte" className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-[#3B62A5] transition-colors duration-200 hover:bg-[#E8F0FE]">
                    Offerte anfragen <ArrowUpRight className="h-4 w-4" />
                  </Link>
                  <a href="tel:+41782238087" data-testid="service-cta-phone" className="mt-4 flex items-center gap-2 text-sm text-white/80 transition-colors duration-200 hover:text-white">
                    <Phone className="h-4 w-4" /> +41 78 223 80 87
                  </a>
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="glass-card rounded-3xl p-7">
                  <h2 className="font-tech text-xs uppercase tracking-[0.25em] text-[#3B62A5] font-semibold mb-4">Weitere Leistungen</h2>
                  <ul className="space-y-1">
                    {others.map((o) => (
                      <li key={o.slug}>
                        <Link to={`/leistungen/${o.slug}`} data-testid={`service-other-${o.slug}`} className="group flex items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium text-slate-700 transition-colors duration-200 hover:bg-white/60 hover:text-[#3B62A5]">
                          {o.title}
                          <ArrowUpRight className="h-4 w-4 text-slate-300 transition-colors duration-200 group-hover:text-[#3B62A5]" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
